export const namespaced = true
import LeaderService from '@/services/LeaderService.js'
import _clone from 'lodash/cloneDeep'
import { getMdsUnities } from '@/utils/common'

const EMPTY_FILTERS = {
  country: {},
  city: {},
  professions: {},
  postGrades: {},
  specialties: {},
  subSpecialties: {},
  profiles: {},
  criteria: {},
  cientificAs: {},
  firstLanguage: {},
  secondLanguage: {},
  category: {},
  range: {}
}

const isDateInRange = (date, range) => {
  if (!date) return false
  const from = new Date(range[0] + ' 00:00').getTime()
  const to = range.length > 1 ? new Date(range[1] + ' 00:00').getTime() : null
  const oneDate = !to
  const inverse = !oneDate && to < from
  const dateTimestamp = new Date(date + ' 00:00').getTime()

  if (oneDate) return from === dateTimestamp
  if (inverse) return to <= dateTimestamp && dateTimestamp <= from
  return from <= dateTimestamp && dateTimestamp <= to
}

const removeDeprecatedLeaderFields = leader => {
  delete leader.expertices
  delete leader.courseG
  return leader
}

export const state = {
  leaders: [],
  leadersMap: {},
  unfilteredLeaders: [],
  leadersLoaded: false,
  openFormDialog: false,
  openProfileDialog: false,
  openFiltersDialog: false,
  openCalendarDialog: false,
  openRemoveDialog: false,
  openPdfDialog: false,
  registering: false,
  selectedLeader: null,
  selectedIndex: null,
  selectedUnfilteredIndex: null,
  selectedPdf: {},
  filters: {},
  dirtyLeaders: true,
  dirtyFilters: false,
  loadingFilters: true,
  applyingFilters: false
}

export const mutations = {
  SET_LEADERS(state, list) {
    state.leaders = list
  },
  SET_UNFILTERED_LEADERS(state, list) {
    state.unfilteredLeaders = list
    state.leadersMap = list.reduce(function(map, obj) {
      map[obj.id] = obj
      return map
    }, {})
  },
  SET_DIRTY_LEADERS(state, val) {
    state.dirtyLeaders = val
  },
  SET_DIRTY_FILTERS(state, val) {
    state.dirtyFilters = val
  },
  SET_LEADERS_LOADED(state, val) {
    state.leadersLoaded = val
  },
  UPDATE_LEADER(state, leader) {
    state.leaders = [
      ...state.leaders.slice(0, state.selectedIndex),
      leader,
      ...state.leaders.slice(state.selectedIndex + 1)
    ]

    state.unfilteredLeaders = [
      ...state.unfilteredLeaders.slice(0, state.selectedUnfilteredIndex),
      leader,
      ...state.unfilteredLeaders.slice(state.selectedUnfilteredIndex + 1)
    ]

    state.leadersMap = { ...state.leadersMap, [leader.id]: leader }
  },
  SET_FILTERS(state, val) {
    state.filters = val
  },
  SET_LOADING_FILTERS(state, val) {
    state.loadingFilters = val
  },
  REMOVE_LEADER(state, leaderId) {
    state.leaders = state.leaders.filter(leader => leader.id !== leaderId)
    state.unfilteredLeaders = state.unfilteredLeaders.filter(
      leader => leader.id !== leaderId
    )
    const leadersMap = { ...state.leadersMap }
    delete leadersMap[leaderId]
    state.leadersMap = leadersMap
  },
  SET_SELECTED_LEADER(state, val) {
    if (state.selectedLeader) {
      state.selectedLeader = null
    }
    state.selectedLeader = val
  },
  SET_SELECTED_INDEX(state, val) {
    state.selectedIndex = val
  },
  SET_SELECTED_UNFILTERED_INDEX(state, val) {
    state.selectedUnfilteredIndex = val
  },
  SET_OPEN_FORM_DIALOG(state, val) {
    state.openFormDialog = val
  },
  SET_OPEN_PROFILE_DIALOG(state, val) {
    state.openProfileDialog = val
  },
  SET_OPEN_FILTERS_DIALOG(state, val) {
    state.openFiltersDialog = val
  },
  SET_OPEN_CALENDAR_DIALOG(state, val) {
    state.openCalendarDialog = val
  },
  SET_OPEN_REMOVE_DIALOG(state, val) {
    state.openRemoveDialog = val
  },
  SET_OPEN_PDF_DIALOG(state, val) {
    state.openPdfDialog = val
  },
  SET_SELECTED_PDF(state, val) {
    state.selectedPdf = val
  },
  ADD_LEADER(state, leader) {
    state.leaders.unshift(leader)
    state.leadersMap = { ...state.leadersMap, [leader.id]: leader }
  },
  SET_REGISTERING(state, val) {
    state.registering = val
  },
  SET_APPLYING_FILTERS(state, val) {
    state.applyingFilters = val
  }
}

export const actions = {
  openFormDialog({ commit, getters }, open) {
    commit('SET_OPEN_FORM_DIALOG', open)
    if (!open && !getters.getOpenProfileDialog()) {
      commit('SET_SELECTED_LEADER', null)
      commit('SET_SELECTED_INDEX', null)
      commit('SET_SELECTED_UNFILTERED_INDEX', null)
    }
  },
  openRemoveDialog({ commit }, open) {
    commit('SET_OPEN_REMOVE_DIALOG', open)
  },
  openPdfDialog({ commit }, open) {
    commit('SET_OPEN_PDF_DIALOG', open)
    if (!open) {
      commit('SET_SELECTED_PDF', {})
    }
  },
  openProfileDialog({ commit }, open) {
    commit('SET_OPEN_PROFILE_DIALOG', open)
    if (!open) {
      commit('SET_SELECTED_LEADER', null)
      commit('SET_SELECTED_INDEX', null)
      commit('SET_SELECTED_UNFILTERED_INDEX', null)
    }
  },
  openFiltersDialog({ commit, getters, dispatch }, open) {
    commit('SET_OPEN_FILTERS_DIALOG', open)
    if (getters.getDirtyLeaders()) {
      commit('SET_LOADING_FILTERS', true)
      dispatch('setFilterLists')
    }
  },
  openCalendarDialog({ commit }, open) {
    commit('SET_OPEN_CALENDAR_DIALOG', open)
  },
  setFilterLists({ commit, getters }) {
    const leaders = getters.getUnfilteredLeaders()
    const getFiltersFromArray = values => {
      let filters = {}
      if (Array.isArray(values) && values.length) {
        values.map(item => {
          filters[item] = item
        })
      }
      return filters
    }

    let filters = _clone(EMPTY_FILTERS)
    if (leaders.length) {
      leaders.map(item => {
        if (item.country) filters.country[item.country] = item.country
        if (item.city)
          filters.city[item.city.trim() + '_' + item.country] = {
            value: item.city.trim(),
            country: item.country
          }
        if (item.firstLanguage)
          filters.firstLanguage[item.firstLanguage] = item.firstLanguage
        if (item.secondLanguage)
          filters.secondLanguage[item.secondLanguage] = item.secondLanguage
        if (item.category) filters.category[item.category] = item.category
        if (item.range) filters.range[item.range] = item.range
        filters.professions = {
          ...filters.professions,
          ...getFiltersFromArray(item.professions)
        }
        filters.postGrades = {
          ...filters.postGrades,
          ...getFiltersFromArray(item.postGrades)
        }
        filters.specialties = {
          ...filters.specialties,
          ...getFiltersFromArray(item.specialties)
        }
        filters.subSpecialties = {
          ...filters.subSpecialties,
          ...getFiltersFromArray(item.subSpecialties)
        }
        filters.profiles = {
          ...filters.profiles,
          ...getFiltersFromArray(item.profiles)
        }
        filters.criteria = {
          ...filters.criteria,
          ...getFiltersFromArray(item.criteria)
        }
        filters.cientificAs = {
          ...filters.cientificAs,
          ...getFiltersFromArray(item.cientificAs)
        }
        filters.unities = {
          ...filters.unities,
          ...getFiltersFromArray(getMdsUnities(item.unities))
        }
      })
    }
    setTimeout(() => {
      commit('SET_FILTERS', filters)
      commit('SET_LOADING_FILTERS', false)
      commit('SET_DIRTY_LEADERS', false)
    })
  },
  clearFilters({ commit, getters }) {
    commit('SET_LEADERS', getters.getUnfilteredLeaders())
    commit('SET_DIRTY_FILTERS', false)
    commit('SET_OPEN_FILTERS_DIALOG', false)
  },
  applyFilters({ commit, getters }, { selected }) {
    let leaders = getters.getUnfilteredLeaders()
    commit('SET_APPLYING_FILTERS', true)

    if (
      selected.country.length ||
      selected.city.length ||
      selected.professions.length ||
      selected.postGrades.length ||
      selected.specialties.length ||
      selected.subSpecialties.length ||
      selected.profiles.length ||
      selected.criteria.length ||
      selected.cientificAs.length ||
      selected.firstLanguage.length ||
      selected.secondLanguage.length ||
      selected.category.length ||
      selected.range.length ||
      selected.unities.length ||
      selected.endContract.length
    ) {
      let filteredLeaders = leaders.filter(item => {
        let include = true
        /* Filtering item array values */
        if (include && selected.professions.length)
          include = selected.professions.some(
            i => item.professions && item.professions.includes(i)
          )
        if (include && selected.postGrades.length)
          include = selected.postGrades.some(
            i => item.postGrades && item.postGrades.includes(i)
          )
        if (include && selected.specialties.length)
          include = selected.specialties.some(
            i => item.specialties && item.specialties.includes(i)
          )
        if (include && selected.subSpecialties.length)
          include = selected.subSpecialties.some(
            i => item.subSpecialties && item.subSpecialties.includes(i)
          )
        if (include && selected.profiles.length)
          include = selected.profiles.some(
            i => item.profiles && item.profiles.includes(i)
          )
        if (include && selected.criteria.length)
          include = selected.criteria.some(
            i => item.criteria && item.criteria.includes(i)
          )
        if (include && selected.cientificAs.length)
          include = selected.cientificAs.some(
            i => item.cientificAs && item.cientificAs.includes(i)
          )
        if (include && selected.unities.length)
          include = selected.unities.some(i =>
            getMdsUnities(item.unities).includes(i)
          )
        if (include && selected.endContract.length)
          include = isDateInRange(item.endContract, selected.endContract)

        /* Filtering item string values */
        if (include && selected.country.length)
          include = selected.country.includes(item.country)
        if (include && selected.city.length)
          include = selected.city.includes(item.city.trim())
        if (include && selected.firstLanguage.length)
          include = selected.firstLanguage.includes(item.firstLanguage)
        if (include && selected.secondLanguage.length)
          include = selected.secondLanguage.includes(item.secondLanguage)
        if (include && selected.category.length)
          include = selected.category.includes(item.category)
        if (include && selected.range.length)
          include = selected.range.includes(item.range)
        return include
      })
      commit('SET_LEADERS', filteredLeaders)
      commit('SET_DIRTY_FILTERS', true)
    } else {
      commit('SET_LEADERS', leaders)
      commit('SET_DIRTY_FILTERS', false)
    }
    setTimeout(() => {
      commit('SET_APPLYING_FILTERS', false)
      commit('SET_OPEN_FILTERS_DIALOG', false)
    })
  },
  fetchLeaders({ commit, dispatch }) {
    commit('SET_LEADERS_LOADED', false)
    LeaderService.getLeaders()
      .then(querySnapshot => {
        let leaders = querySnapshot.docs.map(doc => {
          let item = doc.data()
          item.id = doc.id
          return item
        })
        commit('SET_LEADERS', leaders)
        commit('SET_UNFILTERED_LEADERS', leaders)
        commit('SET_LEADERS_LOADED', true)
        commit('SET_DIRTY_LEADERS', true)
        dispatch('setFilterLists')
      })
      .catch(error => {
        console.error("Couldn't get leaders", error)
      })
  },
  addLeader({ commit }, { leader, pdfFile }) {
    commit('SET_REGISTERING', true)
    leader.createdAt = new Date().getTime()
    leader.updatedAt = new Date().getTime()
    leader.deleted = false
    leader.secondLanguage = leader.secondLanguage
      ? leader.secondLanguage.toUpperCase()
      : ''
    leader.unities = getMdsUnities(leader.unities)
    removeDeprecatedLeaderFields(leader)

    LeaderService.pushLeader(leader, pdfFile)
      .then(doc => {
        var item = _clone(leader)
        item.id = doc.id

        commit('ADD_LEADER', item)
        commit('SET_REGISTERING', false)
        commit('SET_OPEN_FORM_DIALOG', false)
        commit('SET_DIRTY_LEADERS', true)
      })
      .catch(error => {
        console.error('Error writing document: ', error)
        commit('SET_REGISTERING', false)
      })
  },
  updateLeader({ commit, getters }, { leader, pdfFile }) {
    commit('SET_REGISTERING', true)
    leader.updatedAt = new Date().getTime()
    leader.secondLanguage = leader.secondLanguage
      ? leader.secondLanguage.toUpperCase()
      : ''
    leader.unities = getMdsUnities(leader.unities)
    removeDeprecatedLeaderFields(leader)

    LeaderService.updateLeader(leader, pdfFile)
      .then(() => {
        var item = _clone(leader)

        console.info('Success update')
        commit('UPDATE_LEADER', item)
        commit('SET_OPEN_FORM_DIALOG', false)
        commit('SET_REGISTERING', false)
        commit('SET_DIRTY_LEADERS', true)

        if (getters.getOpenProfileDialog()) {
          commit('SET_SELECTED_LEADER', leader)
        } else {
          commit('SET_SELECTED_LEADER', null)
          commit('SET_SELECTED_INDEX', null)
          commit('SET_SELECTED_UNFILTERED_INDEX', null)
        }
      })
      .catch(error => {
        console.error('Error updating document: ', error)
        commit('SET_REGISTERING', false)
      })
  },
  deleteLeader({ commit }, { leader }) {
    leader.deleted = true
    leader.updatedAt = new Date().getTime()
    removeDeprecatedLeaderFields(leader)
    LeaderService.updateLeader(leader)
      .then(() => {
        console.info('Success delete')
        commit('REMOVE_LEADER', leader.id)
        commit('SET_OPEN_REMOVE_DIALOG', false)
        commit('SET_DIRTY_LEADERS', true)
      })
      .catch(error => {
        console.error('Error removing document: ', error)
      })
  },
  openProfile({ commit, getters }, { leader }) {
    var index = getters.getLeaders().indexOf(leader)
    var unfilteredIndex = getters.getUnfilteredLeaders().indexOf(leader)
    commit('SET_SELECTED_LEADER', leader)
    commit('SET_SELECTED_INDEX', index)
    commit('SET_SELECTED_UNFILTERED_INDEX', unfilteredIndex)
    commit('SET_OPEN_PROFILE_DIALOG', true)
  },
  openPdf({ commit }, { url, name }) {
    commit('SET_OPEN_PDF_DIALOG', true)
    commit('SET_SELECTED_PDF', { url, name })
  },
  editLeader({ commit, getters }, { leader }) {
    var index = getters.getLeaders().indexOf(leader)
    var unfilteredIndex = getters.getUnfilteredLeaders().indexOf(leader)

    commit('SET_SELECTED_LEADER', leader)
    commit('SET_SELECTED_INDEX', index)
    commit('SET_SELECTED_UNFILTERED_INDEX', unfilteredIndex)
    commit('SET_OPEN_FORM_DIALOG', true)
  }
}

export const getters = {
  getOpenProfileDialog: state => () => {
    return state.openProfileDialog
  },
  getDirtyLeaders: state => () => {
    return state.dirtyLeaders
  },
  getDirtyFilters: state => () => {
    return state.dirtyFilters
  },
  getLeaders: state => () => {
    return state.leaders
  },
  getUnfilteredLeaders: state => () => {
    return state.unfilteredLeaders
  }
}
